import { FC } from 'react';
import { useTranslation } from 'react-i18next';
import { Label } from '@/components/ui/label';
import { SuperAdminApartmentDetailDto } from '@/features/superAdmin/features/data/types/superAdmin-data.types';
import {
    formatBoolean,
    formatNavValue,
} from '@/features/superAdmin/features/data/shared/utils/superAdminDataFormatters';

type DetailField = {
    label: string;
    value: string;
};

type Props = {
    apartment: SuperAdminApartmentDetailDto;
};

const SuperAdminRoomMainTab: FC<Props> = ({ apartment }) => {
    const { t } = useTranslation();

    const fields: DetailField[] = [
        { label: t('superAdmin.data.columns.id'), value: apartment.id },
        {
            label: t('superAdmin.data.columns.tenant'),
            value: formatNavValue(apartment.tenantId, apartment.tenantName),
        },
        { label: t('superAdmin.data.rooms.columns.name'), value: apartment.name },
        { label: t('superAdmin.data.rooms.columns.description'), value: apartment.description ?? '' },
        {
            label: t('superAdmin.data.columns.hotel'),
            value: formatNavValue(apartment.hotelId, apartment.hotelName),
        },
        {
            label: t('superAdmin.data.columns.owner'),
            value: formatNavValue(apartment.ownerId, apartment.ownerName),
        },
        {
            label: t('superAdmin.data.rooms.columns.apartmentType'),
            value: formatNavValue(apartment.apartmentTypeId, apartment.apartmentTypeName),
        },
        { label: t('superAdmin.data.rooms.columns.cadastralCode'), value: apartment.cadastralCode ?? '' },
        { label: t('superAdmin.data.rooms.columns.squareness'), value: apartment.squareness ?? '' },
        { label: t('superAdmin.data.rooms.columns.electricityId'), value: apartment.electricityId ?? '' },
        { label: t('superAdmin.data.rooms.columns.waterId'), value: apartment.waterId ?? '' },
        { label: t('superAdmin.data.rooms.columns.gasId'), value: apartment.gasId ?? '' },
        { label: t('superAdmin.data.rooms.columns.internetId'), value: apartment.internetId ?? '' },
        {
            label: t('superAdmin.data.rooms.columns.isVirtual'),
            value: formatBoolean(apartment.isVirtual),
        },
        {
            label: t('superAdmin.data.columns.deletionMark'),
            value: formatBoolean(apartment.deletionMark),
        },
        { label: t('superAdmin.data.columns.externalId'), value: apartment.externalId ?? '' },
    ];

    return (
        <div className="rounded-semi-xl border border-popover-100 bg-popover-10 p-6">
            <div className="grid grid-cols-1 gap-6 sm:grid-cols-2">
                {fields.map((field) => (
                    <div key={field.label} className="flex flex-col gap-1.5">
                        <Label className="text-popover-800">{field.label}</Label>
                        <span className="break-all text-sm">{field.value || '—'}</span>
                    </div>
                ))}
            </div>
        </div>
    );
};

export default SuperAdminRoomMainTab;
